import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type DeckCreate, type DeckUpdate, type Format } from '@ugin/shared';

import { api } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';

export function useDecks(format?: Format) {
  return useQuery({
    queryKey: queryKeys.decks.list(format),
    queryFn: () => api.decks.list(format ? { format } : undefined),
  });
}

export function useDeck(id: string | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.decks.detail(id) : ['decks', 'detail', 'none'],
    queryFn: () => (id ? api.decks.get(id) : Promise.reject(new Error('No deck id'))),
    enabled: !!id,
  });
}

export function useDeckAnalysis(id: string | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.decks.analysis(id) : ['decks', 'analysis', 'none'],
    queryFn: () => (id ? api.decks.analysis(id) : Promise.reject(new Error('No deck id'))),
    enabled: !!id,
  });
}

export function useDeckSimilar(id: string | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.decks.similar(id) : ['decks', 'similar', 'none'],
    queryFn: () => (id ? api.decks.similar(id) : Promise.reject(new Error('No deck id'))),
    enabled: !!id,
  });
}

export function useCreateDeck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: DeckCreate) => api.decks.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.decks.all() });
    },
  });
}

export function useUpdateDeck(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: DeckUpdate) => api.decks.update(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.decks.all() });
      void qc.invalidateQueries({ queryKey: queryKeys.decks.detail(id) });
      void qc.invalidateQueries({ queryKey: queryKeys.decks.analysis(id) });
    },
  });
}

export function useDeleteDeck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.decks.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.decks.all() });
    },
  });
}
